import { ActionStatus } from '@common/constants/shared';
import { formValueSelector } from '@common/redux/selectors/formInputs';
import { resetValueForm, setFormValue } from '@common/redux/slice/formInputs';
import { Field, IForm, IObj, ISearchForm, Obj } from '@common/types';
import { getActions } from '@common/utils/handleActions/func/helps';
import { find, forEach, get, isEmpty } from 'lodash';
import moment from 'moment';
import React, {
  createContext,
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Text, TouchableOpacity, View } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import FormField from './FormField';
import { generateSchema, ISearchField } from './schema';
import createStyles from './style';
import SubmitButton from './SubmitButton';
import connectorInput from '@common/components/ConnectorInput';
import { SimpleField } from 'click-types';

const MAX_NUMERIC = 999999999999999;
const MIN_NUMERIC = -999999999999999;
/**
 * Date/Time range
 *
 * Postgres: https://www.postgresql.org/docs/current/datatype-datetime.html
 *
 * Javascript: http://ecma-international.org/ecma-262/5.1/#sec-15.9.1.1
 */
const MAX_DATETIME = new Date(275760, 0, 0, 0, 0, 0, 0);
const MIN_DATETIME = new Date(-4713, 0, 0, 0, 0, 0, 0);
type State = {
  loading: boolean;
  notifications: IObj;
  formError: IObj;
  errors: {
    [key: string]: Array<{
      id: string;
      message: string;
    }>;
  };
};

export const ActionContext = createContext({});
export const useActionContext = () => useContext(ActionContext);

const removeUndefined = (
  value: any,
  valueType: 'fromValue' | 'toValue',
  fieldType: SimpleField
) => {
  if (value === undefined || value === '' || value === null) {
    switch (fieldType) {
      case SimpleField.NUMBER:
        return valueType === 'fromValue' ? MIN_NUMERIC : MAX_NUMERIC;
      case SimpleField.DATE_TIME:
      case SimpleField.DATE_ONLY:
        return valueType === 'fromValue' ? MIN_DATETIME : MAX_DATETIME;
      default:
    }
  }
  return value;
};

const SearchForm: FC<ISearchForm> = (object) => {
  const { onPress, changeInput } = object;
  const dispatch = useDispatch();
  const styles = createStyles(object);
  const reduxFormFields = useSelector(formValueSelector);

  const [state, setState] = useState<State>({
    loading: false,
    errors: {},
    notifications: {},
    formError: {},
  });

  let { fields, submitButton, hidden } = object || {};

  const valuesChanged = useMemo(() => {
    let valueChanges: Obj = {};
    forEach(object.fields, (field: any) => {
      const fieldId = field.fieldId;

      if (field.range) {
        const fromValue = removeUndefined(
          reduxFormFields[`${object.id}-${fieldId}-from`],
          'fromValue',
          field.type
        );
        const toValue = removeUndefined(
          reduxFormFields[`${object.id}-${fieldId}-to`],
          'toValue',
          field.type
        );
        const rangeValues = [fromValue, toValue];
        valueChanges[fieldId] = {
          value: rangeValues,
          searchType: 'range',
        };
      } else {
        const fieldValue = reduxFormFields[`${object.id}-${fieldId}`];
        if (fieldValue !== undefined && fieldValue !== '') {
          if (Array.isArray(fieldValue)) {
            valueChanges[fieldId] = {
              value: fieldValue,
              searchType: 'value',
              comparator: field.comparator,
            };
          } else {
            valueChanges[fieldId] = {
              value: [fieldValue],
              searchType: 'value',
              comparator: field.comparator,
            };
          }
        }
      }
    });

    return valueChanges;
  }, [object, reduxFormFields]);

  const formatDataForSubmission = (
    data: Record<string, any>
  ): Record<string, any> => {
    const formattedData: Record<string, any> = {};

    Object.entries(data).forEach(([key, value]) => {
      if (value && typeof value === 'object' && value.searchType) {
        formattedData[key] = value;
      } else if (Array.isArray(value)) {
        formattedData[key] = {
          value: value,
          searchType: 'value',
        };
      } else if (value !== undefined && value !== '') {
        formattedData[key] = {
          value: [value],
          searchType: 'value',
        };
      }
    });

    return formattedData;
  };

  const handleSubmit = useCallback(async () => {
    setState({
      ...state,
      loading: true,
      errors: {},
    });

    const fields: Array<ISearchField | any> = object.fields;
    const schema = generateSchema(fields, 'ja');
    const dataToPassed = formatDataForSubmission(valuesChanged);
    const dataForValidation: Record<string, any> = {};
    Object.entries(dataToPassed).forEach(([key, value]) => {
      if (value && value.value) {
        dataForValidation[key] =
          value.searchType === 'range' ? value.value.join(',') : value.value[0];
      }
    });
    try {
      const val = await schema.validate(dataForValidation, {
        abortEarly: false,
      });
      if (!val) {
        return;
      }
      setState({ ...state, errors: {} });
      if (changeInput) {
        changeInput(dataToPassed);
      }

      const resp = await onPress(getActions(object.actions), {
        record: dataToPassed, // Pass the formatted data
        formId: object.id,
      });
      if (!resp) return;
      if (!Array.isArray(resp)) {
        setState((props) => ({
          ...props,
          formError: resp?.message,
        }));
      }
      const actionId = getActions(object.actions);
      const actions = get(object, `actions.${actionId}.actions`, []);
      const defaultAction = actions[0];
      const successMessage = find(resp, {
        actionId: defaultAction?.id,
        status: ActionStatus.SUCCESS,
      });

      const errorMessage = find(resp, {
        actionId: defaultAction?.id,
        status: ActionStatus.FAILED,
      });

      if (successMessage) {
        setState((props) => ({
          ...props,
          notifications: {
            ...props.notifications,
            [object.id]: successMessage.message,
          },
        }));
      }
      if (errorMessage) {
        setState((props) => ({
          ...props,
          formError: {
            ...props.formError,
            [object?.id]: errorMessage.message,
          },
        }));
      }
      setTimeout(
        () =>
          setState((props) => ({
            ...props,
            formError: { ...props.formError, [object.id]: '' },
            notifications: { ...props.notifications, [object.id]: '' },
          })),
        2000
      );
    } catch (err: any) {
      let errors: any[] = [];
      err.inner.forEach((e: any) => {
        errors.push({ id: e.path, message: e.message });
      });
      setState({ ...state, errors: { [object.id]: errors }, loading: false });
    }
  }, [object.fields, valuesChanged]);

  const renderMessage = useCallback(() => {
    return (
      <View>
        <Text style={{ color: 'green' }}>
          {get(state, `notifications.${object?.id}`, '')}
        </Text>
      </View>
    );
  }, [state.notifications, object.id]);

  const renderError = useCallback(() => {
    return (
      <View>
        <Text style={{ color: 'red' }}>
          {get(state, `formError.${object?.id}`, '')}
        </Text>
      </View>
    );
  }, [state.formError]);

  const resetForm = useCallback(() => {
    const valueField = object.fields.reduce((pre, curr) => {
      if (curr.range) {
        return {
          ...pre,
          [`${object.id}-${curr.fieldId}-from`]: '',
          [`${object.id}-${curr.fieldId}-to`]: '',
        };
      }
      return { ...pre, [`${object.id}-${curr.fieldId}`]: '' };
    }, {});
    !isEmpty(valueField) && dispatch(resetValueForm(valueField));
  }, [dispatch, object.data]);

  // reset value Form
  useEffect(() => {
    return () => {
      if (!object.resetFormWhenSubmit) {
        return;
      }
      resetForm();
    };
  }, [resetForm]);

  const objectError = get(state.formError, object?.id);
  const objectMessage = get(state.notifications, object?.id);

  return object.database ? (
    <View style={styles.container}>
      {fields &&
        fields.map((field: Field) => {
          const fieldId = field?.fid || field.fieldId;
          // * check if field has an error
          const hasError = (state.errors[object.id] || []).find(
            (err) => err.id === fieldId
          );

          return (
            <FormField
              key={fieldId}
              object={object}
              field={field}
              active={hidden}
              hasError={hasError}
            />
          );
        })}
      {objectError ? renderError() : null}
      {!objectError && objectMessage ? renderMessage() : null}

      <SubmitButton
        {...submitButton}
        marginTop={fields.length ? 20 : 0}
        onSubmit={handleSubmit}
        submitting={state.loading}
        disabled={state.loading}
      />
      {object.resetText ? (
        <TouchableOpacity
          style={{
            marginTop: 10,
          }}
          onPress={() => {
            resetForm();
          }}
        >
          <Text
            style={{
              color: 'black',
              textDecorationLine: 'underline',
              textAlign: 'center',
              lineHeight: 20,
              fontSize: submitButton.fontSize,
            }}
            numberOfLines={0}
          >
            {object.resetText}
          </Text>
        </TouchableOpacity>
      ) : null}
    </View>
  ) : (
    <></>
  );
};
export default connectorInput(SearchForm);
